import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, Drawer, Stack, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import Button from '../../Button/Button';
import IconButton from '../../IconButton/IconButton';
import { getFormDrawerTransitionConfig } from './drawerMotion';
import FormConfirmDialog from './FormConfirmDialog';

const DEFAULT_WIDTH = { xs: '100%', sm: 480, md: 560 };

/**
 * Right-side form panel shell — header, scrollable body, sticky footer.
 * Pair with FormBuilder or pass custom children. No validation/API logic here.
 */
export default function FormDrawer({
  open,
  onClose,
  title,
  subtitle,
  headerBadge = null,
  children,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  onSubmit,
  isSubmitting = false,
  submitDisabled = false,
  disableClose = false,
  confirmBeforeClose = false,
  confirmMessage = 'You have unsaved changes. Close without saving?',
  confirmTitle = 'Discard changes?',
  confirmCancelLabel = 'Keep editing',
  confirmConfirmLabel = 'Discard changes',
  showCloseButton = true,
  showFooter = true,
  width = DEFAULT_WIDTH,
  ariaLabelledby,
}) {
  const { semantic, typography } = useFeatureTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {
    noSsr: true,
  });
  const { duration, easing, backdropDuration } = useMemo(
    () => getFormDrawerTransitionConfig(prefersReducedMotion),
    [prefersReducedMotion]
  );
  const generatedTitleId = useId();
  const titleId = ariaLabelledby || generatedTitleId;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingCloseRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setConfirmOpen(false);
      pendingCloseRef.current = null;
    }
  }, [open]);

  const finishClose = useCallback(
    (event, reason) => {
      pendingCloseRef.current = null;
      setConfirmOpen(false);
      onClose?.(event, reason);
    },
    [onClose]
  );

  const requestClose = useCallback(
    (event, reason) => {
      if (disableClose) {
        return;
      }
      if (confirmBeforeClose) {
        pendingCloseRef.current = { event, reason };
        setConfirmOpen(true);
        return;
      }
      finishClose(event, reason);
    },
    [confirmBeforeClose, disableClose, finishClose]
  );

  const handleConfirmDiscard = useCallback(() => {
    const pending = pendingCloseRef.current;
    finishClose(pending?.event, pending?.reason ?? 'confirmDiscard');
  }, [finishClose]);

  const handleConfirmCancel = useCallback(() => {
    pendingCloseRef.current = null;
    setConfirmOpen(false);
  }, []);

  const handleDrawerClose = useCallback(
    (event, reason) => {
      if (disableClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
        return;
      }
      requestClose(event, reason);
    },
    [disableClose, requestClose]
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      onSubmit?.(event);
    },
    [onSubmit]
  );

  const slotProps = useMemo(
    () => ({
      root: {
        // Avoid body padding compensation when the scrollbar hides — a common "jerk" on open.
        disableScrollLock: true,
      },
      backdrop: {
        transitionDuration: backdropDuration,
      },
      transition: {
        // MUI skips appear on first mount by default; always animate open/close.
        appear: true,
        easing,
      },
      paper: {
        'aria-labelledby': titleId,
        sx: {
          width,
          maxWidth: '100vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: semantic.surfaces.surface,
          color: semantic.text.primary,
        },
      },
    }),
    [
      backdropDuration,
      easing,
      semantic.surfaces.surface,
      semantic.text.primary,
      titleId,
      width,
    ]
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        variant="temporary"
        role="presentation"
        transitionDuration={duration}
        slotProps={slotProps}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          autoComplete="off"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            height: '100%',
          }}
        >
          {(title || subtitle || headerBadge || showCloseButton) && (
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                flexShrink: 0,
                px: 3,
                py: 2,
                borderBottom: `1px solid ${semantic.borders.border}`,
                bgcolor: semantic.surfaces.surface,
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {(title || headerBadge) && (
                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                      {title ? (
                        <Typography
                          id={titleId}
                          component="h2"
                          sx={{
                            fontSize: typography.fontSizeLg,
                            fontWeight: typography.fontWeightSemibold,
                            color: semantic.text.primary,
                            lineHeight: 1.35,
                          }}
                        >
                          {title}
                        </Typography>
                      ) : null}
                      {headerBadge}
                    </Stack>
                  )}
                  {subtitle ? (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: title || headerBadge ? 0.5 : 0,
                        color: semantic.text.secondary,
                        fontSize: typography.fontSizeSm,
                        lineHeight: 1.45,
                      }}
                    >
                      {subtitle}
                    </Typography>
                  ) : null}
                </Box>
                {showCloseButton ? (
                  <IconButton
                    aria-label="Close drawer"
                    onClick={requestClose}
                    disabled={disableClose}
                    size="sm"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </Stack>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              overscrollBehavior: 'contain',
              scrollbarGutter: 'stable',
              px: 3,
              py: 2.5,
              bgcolor: semantic.surfaces.surfaceSubtle ?? semantic.surfaces.background,
            }}
          >
            {children}
          </Box>

          {showFooter ? (
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                flexShrink: 0,
                px: 3,
                py: 2,
                borderTop: `1px solid ${semantic.borders.border}`,
                bgcolor: semantic.surfaces.surface,
              }}
            >
              <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={requestClose}
                  disabled={disableClose}
                >
                  {cancelLabel}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  disabled={isSubmitting || submitDisabled}
                >
                  {submitLabel}
                </Button>
              </Stack>
            </Box>
          ) : null}
        </Box>
      </Drawer>

      <FormConfirmDialog
        open={confirmOpen}
        onCancel={handleConfirmCancel}
        onConfirm={handleConfirmDiscard}
        title={confirmTitle}
        message={confirmMessage}
        cancelLabel={confirmCancelLabel}
        confirmLabel={confirmConfirmLabel}
      />
    </>
  );
}
